import api from './axios';

export const login = (data) => api.post('/api/auth/login', data);
export const getMe = () => api.get('/api/auth/me');

export const getStudents = () => api.get('/api/admin/students');
export const createStudent = (data) => api.post('/api/admin/students', data);
export const updateStudent = (id, data) => api.put(`/api/admin/students/${id}`, data);
export const deleteStudent = (id) => api.delete(`/api/admin/students/${id}`);
export const getStudentTests = (studentId) => api.get(`/api/admin/students/${studentId}/tests`);

export const getSubjects = () => api.get('/api/subjects');
export const createSubject = (data) => api.post('/api/subjects', data);
export const getChapters = (subjectId) => api.get(`/api/subjects/${subjectId}/chapters`);
export const createChapter = (subjectId, data) => api.post(`/api/subjects/${subjectId}/chapters`, data);
export const getTopics = (chapterId) => api.get(`/api/chapters/${chapterId}/topics`);
export const createTopic = (chapterId, data) => api.post(`/api/chapters/${chapterId}/topics`, data);

export const getQuestions = (params) => api.get('/api/questions', { params });
export const createQuestion = (data) => api.post('/api/questions', data);
export const updateQuestion = (id, data) => api.put(`/api/questions/${id}`, data);
export const deleteQuestion = (id) => api.delete(`/api/questions/${id}`);

export const createTest = (data) => api.post('/api/tests', data);
export const getTests = () => api.get('/api/tests');
export const getTest = (id) => api.get(`/api/tests/${id}`);
export const submitTest = (id, data) => api.post(`/api/tests/${id}/submit`, data);
export const getResult = (id) => api.get(`/api/tests/${id}/result`);
export const getAdminResult = (id) => api.get(`/api/tests/${id}/admin-result`);

export const getVideos = (chapterId) => api.get('/api/videos', { params: { chapter_id: chapterId } });
export const createVideo = (data) => api.post('/api/videos', data);
export const updateVideo = (id, data) => api.put(`/api/videos/${id}`, data);
export const deleteVideo = (id) => api.delete(`/api/videos/${id}`);
export const reorderVideos = (data) => api.patch('/api/videos/reorder', data);

export const getCards = (chapterId) => api.get('/api/cards', { params: { chapter_id: chapterId } });
export const createCard = (data) => api.post('/api/cards', data);
export const updateCard = (id, data) => api.put(`/api/cards/${id}`, data);
export const deleteCard = (id) => api.delete(`/api/cards/${id}`);
export const getPracticeCards = () => api.get('/api/cards/practice');
export const attemptCard = (id, data) => api.post(`/api/cards/${id}/attempt`, data);
export const getCardPreferences = () => api.get('/api/cards/preferences');
export const updateCardPreferences = (data) => api.put('/api/cards/preferences', data);

export const unlockAttempt = (testId, questionId, data) => api.post(`/api/tests/${testId}/questions/${questionId}/unlock-attempt`, data);
export const retireQuestion = (testId, questionId) => api.post(`/api/tests/${testId}/questions/${questionId}/retire`);
export const unretireQuestion = (testId, questionId) => api.delete(`/api/tests/${testId}/questions/${questionId}/retire`);
export const getRetiredCount = () => api.get('/api/tests/retired/count');
export const flagQuestionError = (questionId) => api.post(`/api/questions/${questionId}/flag-error`);

export const generateExplanation = (questionId) => api.post(`/api/questions/${questionId}/generate-explanation`);
export const listManualReviews = () => api.get('/api/admin/manual-reviews');
export const resolveManualReview = (questionId, data) => api.post(`/api/admin/manual-reviews/${questionId}/resolve`, data);

export const lookupQuestions = (data) => api.post('/api/admin/questions/lookup', data);

// Weak-Module Practice (Daily Target)
export const getDailyTargets = () => api.get('/api/practice/today');
export const getPracticeModules = (scope = 'weak') => api.get('/api/practice/modules', { params: { scope } });
export const startPractice = (module_code) => api.post('/api/practice/start', { module_code });
export const submitPractice = (data) => api.post('/api/practice/submit', data);

// Chapterwise Tests — fixed papers per chapter, retakeable any number of times
export const getChapterTests = (subjectId) =>
  api.get('/api/chapter-tests', { params: subjectId ? { subject_id: subjectId } : {} });
export const startChapterTest = (setId) => api.post(`/api/chapter-tests/${setId}/start`);
export const getChapterTestAttempts = (setId) => api.get(`/api/chapter-tests/${setId}/attempts`);
export const rebuildChapterTests = (params) => api.post('/api/chapter-tests/rebuild', null, { params });

// Daily Test — one system-generated 90-question paper a day
export const getDailyTest = () => api.get('/api/daily/today');
export const getDailyTestHistory = () => api.get('/api/daily/history');
export const buildDailyTest = (force) => api.post('/api/daily/build', null, { params: { force: !!force } });
export const getDailyStatus = () => api.get('/api/daily/status');

// Module tests ever created (all reattemptable)
export const getCreatedModuleTests = () => api.get('/api/practice/created');

// Admin — chapter completion + notes
export const getChapterCompletion = (subjectId) =>
  api.get('/api/admin/chapters/completion', { params: subjectId ? { subject_id: subjectId } : {} });
export const uploadChapterNotes = (chapterId, file, markComplete = true) => {
  const form = new FormData();
  form.append('file', file);
  form.append('mark_complete', markComplete ? 'true' : 'false');
  return api.post(`/api/admin/chapters/${chapterId}/notes`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getChapterNotes = (chapterId) => api.get(`/api/admin/chapters/${chapterId}/notes`);
export const setChapterComplete = (chapterId, complete) =>
  api.post(`/api/admin/chapters/${chapterId}/complete`, null, { params: { complete } });

// ---- Daily Test ----
// Cumulative on-demand paper built from the concepts the teacher has logged.
// Untimed; answers are saved to the server one at a time as the student picks them.
// NOTE: the plain `getDailyStatus` / `getDailyTest` names above belong to the older
// auto-built 90-question Daily Test, so everything here carries a DailyPaper prefix.
export const getDailyPaperSubjects = () => api.get('/api/daily-paper/subjects');
export const getDailyPaperStatus = () => api.get('/api/daily-paper/status');
export const generateDailyPaper = (subjectId, count = 45) =>
  api.post('/api/daily-paper/generate', { subject_id: subjectId, count });
export const getDailyPaper = (testId) => api.get(`/api/daily-paper/${testId}`);
export const saveDailyPaperAnswer = (testId, data) => api.patch(`/api/daily-paper/${testId}/answer`, data);
export const submitDailyPaper = (testId) => api.post(`/api/daily-paper/${testId}/submit`, {});
export const tagDailyPaperError = (testId, data) => api.post(`/api/daily-paper/${testId}/tag`, data);
export const getDailyPaperHistory = (limit = 20) => api.get('/api/daily-paper/history', { params: { limit } });

// Admin — the teacher's log of which concepts were taught on which day
export const logTaughtConcepts = (data) => api.post('/api/daily-paper/taught', data);
export const getTaughtConcepts = (params) => api.get('/api/daily-paper/taught', { params });
